"use client"
import React, { useState } from 'react'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { motion, AnimatePresence } from "motion/react"
import { pick, type Locale } from '@/lib/i18n'
import { developersDict } from '@/lib/i18n/dicts/developers'

interface DevelopersClientProps {
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

// 飞书完整接入文档（导航「快速接入」已降级为本页内的文档外链，见改版方案 §5.6）
const FEISHU_DOC_URL = 'https://siityjq4bl7.feishu.cn/docx/Tk2DdviSgoF7ONxbAONcDpO3nLe'

// 开发者接入指南页：Hero → 3 步接入流程 → 能力清单 → 完整文档外链 → FAQ → CTA
export default function DevelopersClient({ locale, footer }: DevelopersClientProps) {
    const t = pick(developersDict, locale)
    const [openFaq, setOpenFaq] = useState(-1)

    return (
        <>
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 right-[-140px] h-[340px] w-[340px] rounded-full bg-cyan-400/10 blur-[110px]" />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-12 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-indigo-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shine">
                                {t.hero.titleB}
                            </span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                </div>
            </section>

            {/* 3 步接入流程（样式参考 solutions 步骤流） */}
            <section className="bg-slate-950 pb-20 pt-4">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">
                            {t.steps.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">{t.steps.titleB}</span>
                        </h2>
                    </BlurFade>
                    {/* 步骤卡 flex-1 均分整行，与下方能力清单行总宽一致 */}
                    <div className="mt-10 flex flex-wrap items-stretch justify-center gap-3">
                        {t.steps.items.map((step, i) => (
                            <React.Fragment key={step.title}>
                                {i > 0 && (
                                    <span className="self-center text-indigo-400/60">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                        </svg>
                                    </span>
                                )}
                                <BlurFade delay={0.08 * i} inView className="flex-1 min-w-[240px]">
                                    <div className="group relative h-full w-full rounded-2xl bg-slate-900 ring-1 ring-white/10 px-6 py-6 text-center transition-all duration-300 hover:-translate-y-1 hover:ring-indigo-400/50 hover:shadow-xl hover:shadow-indigo-500/10">
                                        <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-sm font-black text-white shadow-lg shadow-indigo-600/30 animate-breathe">
                                            {i + 1}
                                        </span>
                                        <p className="mt-3 text-sm font-bold text-white group-hover:text-indigo-300 transition-colors duration-300">{step.title}</p>
                                        <p className="mt-2 text-xs leading-5 text-slate-400">{step.desc}</p>
                                    </div>
                                </BlurFade>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            {/* 能力清单 */}
            <section className="bg-slate-950 pb-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">
                            {t.capabilities.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">{t.capabilities.titleB}</span>
                        </h2>
                    </BlurFade>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {t.capabilities.items.map((item, i) => (
                            <BlurFade key={item.label} delay={0.08 * i} inView>
                                <div className="h-full rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 text-center transition-all duration-300 hover:-translate-y-1.5 hover:ring-indigo-400/50 hover:shadow-2xl hover:shadow-indigo-500/15">
                                    <p className="text-xs font-medium tracking-widest text-slate-500 uppercase">{item.label}</p>
                                    <p className="mt-3 text-lg font-black bg-gradient-to-br from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
                                        {item.value}
                                    </p>
                                </div>
                            </BlurFade>
                        ))}
                    </div>

                    {/* 完整文档（飞书外链） */}
                    <BlurFade delay={0.1} inView>
                        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-fuchsia-500/10 to-cyan-400/10 ring-1 ring-indigo-400/25 px-6 py-6">
                            <div className="text-center sm:text-left">
                                <h3 className="text-base font-bold text-white">{t.docs.title}</h3>
                                <p className="mt-1 text-xs md:text-sm text-slate-400">{t.docs.desc}</p>
                            </div>
                            <a href={FEISHU_DOC_URL} target="_blank" rel="noreferrer"
                                className="inline-flex flex-none items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-600/30 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                                {t.docs.button}
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        </div>
                    </BlurFade>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-slate-950 pb-20">
                <div className="max-w-3xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">{t.faq.title}</h2>
                        <p className="mt-3 text-center text-xs md:text-sm text-slate-500">{t.faq.desc}</p>
                    </BlurFade>
                    <div className="mt-8 space-y-3">
                        {t.faq.items.map((faq, i) => {
                            const open = openFaq === i
                            return (
                                <BlurFade key={faq.q} delay={0.06 * i} inView>
                                    <div className={`rounded-2xl ring-1 overflow-hidden transition-colors duration-300 ${open ? 'bg-slate-900 ring-indigo-400/40' : 'bg-slate-900/60 ring-white/10'}`}>
                                        <button
                                            onClick={() => setOpenFaq(open ? -1 : i)}
                                            className="w-full flex items-center justify-between px-5 py-4 text-left"
                                        >
                                            <span className="text-sm font-medium text-slate-100 pr-4">{faq.q}</span>
                                            <motion.svg
                                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                                                className="h-4 w-4 shrink-0 text-indigo-400"
                                                animate={{ rotate: open ? 180 : 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                            </motion.svg>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {open && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                >
                                                    <p className="px-5 pb-5 text-sm leading-6 text-slate-400">{faq.a}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </BlurFade>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-slate-950 pb-24">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-indigo-600 bg-[length:200%_auto] p-10 md:p-14 text-center shadow-2xl shadow-indigo-600/30">
                            <h2 className="text-2xl md:text-3xl font-black text-white">{t.cta.title}</h2>
                            <a href="../contact2"
                                className="mt-6 inline-block rounded-full bg-white px-8 py-3 text-sm font-bold text-indigo-700 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                                {t.cta.button}
                            </a>
                        </div>
                    </BlurFade>
                </div>
            </section>

            {footer}
        </>
    )
}
